/* Copyright (c) 2021 Paul Nebel */

import { chordParserFactory } from 'chord-symbol';

export const chordProperties = {
    C: { semitones: 0, minorChord: 'C' },
    'C#': { semitones: 1, minorChord: 'C#' },
    Db: { semitones: 1, minorChord: 'C#' },
    D: { semitones: 2, minorChord: 'D' },
    'D#': { semitones: 3, minorChord: 'D#' },
    Eb: { semitones: 3, minorChord: 'Eb' },
    E: { semitones: 4, minorChord: 'E' },
    F: { semitones: 5, minorChord: 'F' },
    'F#': { semitones: 6, minorChord: 'F#' },
    Gb: { semitones: 6, minorChord: 'F#' },
    G: { semitones: 7, minorChord: 'G' },
    'G#': { semitones: 8, minorChord: 'G#' },
    Ab: { semitones: 8, minorChord: 'G#' },
    A: { semitones: 9, minorChord: 'A' },
    'A#': { semitones: 10, minorChord: 'A#' },
    Bb: { semitones: 10, minorChord: 'Bb' },
    B: { semitones: 11, minorChord: 'B' },
};

// create an ENUM
const Format = {
    CHORD_SOUNDS: 1,
    CHORD_STAVE: 2,
};
Object.freeze(Format);
export const Mode = {
    MAJOR: 0,
    MINOR: 1,
    MIXED: 2,
};
Object.freeze(Mode);

const formatParsedChord = (parsedChord, format, accidentals = []) => {
    return parsedChord.notes.map((n, idx, arr) => {
        let octave = '4';
        if (
            idx > 0 &&
            chordProperties[arr[0]].semitones + parsedChord.semitones[idx] > 11
        ) {
            octave = '5';
            // special case for G#
            if (n.toLowerCase() === 'b#') {
                octave = '4';
            }
        }
        if (accidentals) {
            accidentals.push(n.split('').slice(1).join(''));
        }
        let formattedNote = n;
        switch (format) {
            case Format.CHORD_SOUNDS:
                formattedNote = n + octave;
                break;
            case Format.CHORD_STAVE:
                formattedNote = n.toLowerCase() + '/' + octave;
                break;
            default:
                break;
        }
        return formattedNote;
    });
};

export const formatChordSounds = (
    chordSymbol,
    accidentals = [],
    detuneBy = 0
) => {
    let targetChordSymbol = chordSymbol;
    if (detuneBy !== 0) {
        let targetChord = getRootNote(chordSymbol, true);
        let detunedSemitone = chordProperties[targetChord].semitones + detuneBy;
        if (detunedSemitone < 0) {
            detunedSemitone = 12 + detunedSemitone;
        } else if (detunedSemitone > 11) {
            detunedSemitone = detunedSemitone - 12;
        }
        const isDetuned = (prop) => prop.semitones === detunedSemitone;
        const indexOfDetunedChord =
            Object.values(chordProperties).findIndex(isDetuned);
        targetChord = Object.keys(chordProperties)[indexOfDetunedChord];
        targetChordSymbol = parseChord(
            targetChord,
            isMinor(chordSymbol) ? Mode.MINOR : Mode.MAJOR
        );
    }

    return formatParsedChord(
        targetChordSymbol.normalized,
        Format.CHORD_SOUNDS,
        accidentals
    );
};

export const formatChordStave = (chordSymbol, accidentals = []) => {
    return formatParsedChord(
        chordSymbol.normalized,
        Format.CHORD_STAVE,
        accidentals
    );
};

export const formatPianoKeys = (chordSymbol) => {
    const normalizedChord = chordSymbol.normalized;
    let normalizedSemitones = normalizedChord.semitones;
    // allow for inversions
    const bassNote = normalizedChord.bassNote;
    if (bassNote) {
        const bassNotePosition = normalizedChord.notes.indexOf(bassNote);
        if (bassNotePosition < 0) {
            throw new Error(`${chordSymbol} does not seem to be a chord`);
        } else {
            // this will need to be adjusted!!!!......................................................
            normalizedSemitones = normalizedChord.semitones
                .slice(bassNotePosition)
                .map((st) => st - 12)
                .concat(normalizedChord.semitones.slice(0, bassNotePosition));
        }
    }
    // const offset = this.offsets[normalizedChord.rootNote.toLowerCase()] + ((this.props['oct-count'] > 0 ? 1 : 0) * 12);
    // const offset = this.offsets[normalizedChord.rootNote.toLowerCase()];
    const offset = chordProperties[normalizedChord.rootNote].semitones + 1;
    return normalizedSemitones.map(
        (semitone) => parseInt(semitone, '10') + offset
    );
};

export const parseChord = (chord, mode = Mode.MAJOR) => {
    if (chord) {
        const parseChord = chordParserFactory();
    
        if (mode === Mode.MINOR) {
            chord = chordProperties[chord].minorChord + 'm';
        }
        const chordSymbol = parseChord(chord);
    
        if (!chord.error) {
            return chordSymbol;
        } else {
            throw new Error(chord.error[0].message);
        }
    }
};

const getProperty = (chordSymbol, propertyName) => {
    let result = '';
    if (chordSymbol !== undefined) {
        const normalizedChordSymbol = chordSymbol.normalized;
        if (normalizedChordSymbol) {
            result = normalizedChordSymbol[propertyName];
        }
    }
    return result;
};

export const getRootNote = (chordSymbol, ignoreMode = false) => {
    let rootNote = getProperty(chordSymbol, 'rootNote');
    if (!ignoreMode && isMinor(chordSymbol)) {
        rootNote = chordProperties[rootNote].minorChord + 'm';
    }
    return rootNote;
};

export const isMinor = (chordSymbol) => {
    return getProperty(chordSymbol, 'quality') === 'minor';
};
